import React, { useState } from "react";
import style from "./index.module.scss";
import { Button, Modal, Icon, Divider, Spin, message } from "antd";
import RandomItem from "../RandomItem";
import TopOperations from "./components/TopOperations";
import {
    getMyQuestionTypeCount,
    saveExamPaperByStrategy,
    SearchParams
} from "service/answerStore";
import useRequestFn from "utils/useRequestFn";
import { initCountListAtom, initList } from "../../store";
import { useAtom } from "jotai";
import { errorMessage } from "service/service";
import { ResultMessage } from "components/ResultMessage";
import _ from "underscore";
import { handleScore } from "utils/common";
import Decimal from "decimal.js";

interface RandomAnswerProps {
    visible: boolean; //显示 状态
    onCancel: () => void; //关闭
    examId: string; //考核id
}

export default function RandomAnswer({
    visible,
    onCancel,
    examId
}: RandomAnswerProps) {
    const [countList, setCountList] = useAtom(initCountListAtom); //题库数据列表
    const [searchParams, setSearchParams] = useState<SearchParams>(); //查询项参数
    const [disabled, setDisabled] = useState(true); //只读状态

    //总题数
    const topicNums = _.reduce(
        _.pluck(countList, "counts"),
        function (memo, num) {
            return memo + num;
        },
        0
    );

    //总分数
    const totalScores = _.reduce(
        countList,
        function (memo, num) {
            const _memo = new Decimal(memo);
            const _score = new Decimal(num.score);
            const _counts = new Decimal(num.counts);
            return _memo.add(_score.mul(_counts)).toNumber();
        },
        0
    );

    const [getCountList, { loading }] = useRequestFn(getMyQuestionTypeCount);
    const [saveList, { loading: saveLoading }] = useRequestFn(
        saveExamPaperByStrategy
    );

    //获取题库各题型数据
    const onFinish = async (params: SearchParams) => {
        setSearchParams(params);
        try {
            const data = await getCountList(params);
            //将初始值和接口返回的值合并
            const list = initList.map(counts => {
                const item = data.find(item => item.typeId === counts.typeId);
                if (!item) return counts;
                return {
                    ...counts,
                    typeCount: item.typeCount,
                    score: item.score
                };
            });

            setCountList(list);
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    };

    //选题数目，分值等发生变化时立即调用
    const onChange = (data: {
        typeId: string;
        typeCount: number;
        counts: number;
        score: number;
    }) => {
        //处理输入的题数，不大于最大题目数，不小于0
        const typeCount = handleScore(
            parseFloat(String(data.typeCount)),
            data.counts
        );
        //处理分值，单题分值不大于99，不小于1
        const score = handleScore(parseFloat("100"), data.score);

        const newCountLIst = countList.map(item => {
            if (item.typeId === data.typeId) {
                return { ...item, counts: typeCount, score: score };
            }
            return item;
        });

        setCountList([...newCountLIst]);

        //判断当前是否选中题目数是否为0，如果题目数为0则禁止提交，提交按钮置灰
        const length = newCountLIst.filter(item => item.counts !== 0).length;
        if (length !== 0) {
            setDisabled(false);
        } else {
            setDisabled(true);
        }
    };

    //保存并生成随机试卷
    const onOk = async () => {
        const params = countList.map(item => ({
            typeId: item.typeId,
            typeCount: item.counts,
            score: item.score
        }));

        const allParams = {
            examId,
            ...searchParams,
            data: params
        };

        try {
            await saveList(allParams);
            message.success("随机组卷成功");
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        } finally {
            //重置
            onClose();
        }
    };

    //重置
    const onClose = () => {
        setCountList(initList);
        setSearchParams(undefined);
        onCancel();
    };

    const radomTitle = (
        <div>
            <span>随机组卷</span>
            <Icon className={style.radomTitleIcon} type="info-circle" />
            <span className={style.radomTitle}>单次选题最多选100题</span>
        </div>
    );

    const randomFooter = (
        <div className={style.randomFooter}>
            <span>总记</span>
            <span className={style.randomFooterBlue}>{topicNums}</span>
            <span>题</span>
            <Divider className={style.randomFooterDivider} type="vertical" />
            <span>共记</span>
            <span className={style.randomFooterRed}>{totalScores}</span>
            <span>分</span>
            <Button
                className={style.randomFooterBtn}
                type="primary"
                onClick={onOk}
                disabled={disabled}
                loading={saveLoading}>
                确认组卷
            </Button>
        </div>
    );

    return (
        <Modal
            title={radomTitle}
            visible={visible}
            width={600}
            className={style.root}
            onCancel={onClose}
            footer={randomFooter}
            maskClosable={false}
            destroyOnClose>
            <TopOperations onFinish={onFinish} />

            <Spin spinning={loading}>
                {countList?.map(item => (
                    <RandomItem
                        key={item.typeId}
                        {...item}
                        onChange={onChange}
                    />
                ))}
            </Spin>
        </Modal>
    );
}
